import {
  v1PeopleSearchList,
  v1ProfileInfoNDetail,
  v1SportsList,
} from '@on3/api';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/index';
import { type IPageProps } from '@on3/ui-lib/types/common-props';
import { generateKey } from '@on3/ui-lib/utils/generateKey';
import {
  SearchPage,
  type SearchPageProps,
} from 'components/GetRecruited/SearchPage';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { type GetServerSideProps } from 'next';
import Script from 'next/script';
import { createKey } from 'utils/meta';

interface IRecruitSearchPageProps extends IPageProps, SearchPageProps {}

const Search = ({
  siteData,
  pageData,
  profileData,
  profileResults,
  sports,
  ...props
}: IRecruitSearchPageProps) => {
  const filteredSiteData = {
    ...siteData,
    menuData: null,
    secondaryMenuData: null,
  };

  return (
    <PageWrapper
      description="On3 offers athletes and families all the tools they need to get exposure and navigate the recruiting process and NIL"
      hideAds
      siteData={filteredSiteData}
      title="On3 Recruits"
      {...props}
    >
      <SearchPage
        key={createKey(
          profileData?.collectedProfileInfo?.nonce,
          pageData?.page,
          pageData?.rk,
        )}
        profileData={profileData}
        profileResults={profileResults}
        sports={sports}
      />
      <Script
        id="ze-snippet"
        src="https://static.zdassets.com/ekr/snippet.js?key=18c3bdc8-41d8-48c0-84fc-3770b4b22cee"
        strategy="afterInteractive"
      />
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps<SearchPageProps> = async (
  ctx,
) => {
  const recruitsSiteKey = 409;
  const siteData = await withValidUrl.isValid(ctx, recruitsSiteKey);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) return { notFound: true };

  const {
    key,
    nonce,
    page = '1',
    rk = generateKey(),
  } = ctx.query as {
    key: string;
    nonce: string;
    page?: string;
    rk?: string;
  };

  const props: IRecruitSearchPageProps = {
    pageType: 'recruits',
    siteData,
    userData,
  };

  try {
    props.sports = await v1SportsList(proxyApi);

    if (key && nonce) {
      const profileData = await v1ProfileInfoNDetail(proxyApi, +key, nonce);
      const {
        collectedProfileInfo: {
          firstName,
          lastName,
          highSchoolOrganizationKey,
          highSchoolClassYear,
        },
      } = profileData;

      const profileResults = await v1PeopleSearchList(proxyApi, {
        firstName,
        lastName,
        minClassYear: highSchoolClassYear,
        highSchoolOrgKeys: [highSchoolOrganizationKey ?? []].flat(),
        page: +page,
        limit: 5,
      });

      return {
        props: {
          ...props,
          pageData: {
            page,
            rk,
          },
          profileData,
          profileResults,
        },
      };
    }
  } catch (error) {
    console.error(error);
  }

  return {
    props,
  };
};

export default withValidUrl(Search);
